/* ============================================================
   SAM-1072 — pure model of the New event flow (Event · When & where · Contact & access · Review):
   what each step needs, the one-start/one-end schedule, "Copy from English" (a copy, not a
   translation) with its Undo, and the Review summary lines. The screens live in NewEventWizard.tsx.
   ============================================================ */
import { formatEventDate, type EventDayRow, type EventForm } from '../eventForm';

export type EventStep = 'event' | 'when' | 'contact' | 'review';

export const EVENT_STEPS: Array<{ key: EventStep; label: string; title: string }> = [
  { key: 'event', label: 'Event', title: 'event' },
  { key: 'when', label: 'When & where', title: 'when and where' },
  { key: 'contact', label: 'Contact & access', title: 'contact and access' },
  { key: 'review', label: 'Review', title: 'review' },
];

export const eventStepIndex = (step: EventStep): number => EVENT_STEPS.findIndex((s) => s.key === step);

// --- Dates: the form keeps `YYYY-MM-DDTHH:mm` (local), the page shows a day and a time ---------------

export const splitLocal = (value?: string | null): { date: string; time: string } => {
  const [date = '', time = ''] = (value || '').split('T');
  return { date, time: time.slice(0, 5) };
};

export const joinLocal = (date: string, time: string): string => (date ? `${date}T${time || '00:00'}` : '');

/** Times every 15 minutes, as the picker lists them. */
export const TIME_OPTIONS: string[] = Array.from({ length: 96 }, (_, i) => {
  const h = Math.floor(i / 4);
  const m = (i % 4) * 15;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
});

/** "8:30 AM" */
export const formatTime12 = (hhmm: string): string => {
  const [h, m] = hhmm.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return '';
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

const toMinutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

const dayNumber = (iso: string): number => {
  const [y, mo, d] = iso.split('-').map(Number);
  return Math.round(Date.UTC(y, (mo || 1) - 1, d || 1) / 86_400_000);
};

/** Days from the first to the last day, both counted (same day = 1); 0 when a day is missing. */
export const dayCount = (firstDay: string, lastDay: string): number =>
  firstDay && lastDay ? dayNumber(lastDay) - dayNumber(firstDay) + 1 : 0;

/** End time two hours after the start (capped at 23:45), the default the mock proposes. */
export const defaultEndTime = (start: string): string => {
  const end = Math.min(toMinutes(start) + 120, 23 * 60 + 45);
  return `${String(Math.floor(end / 60)).padStart(2, '0')}:${String(end % 60).padStart(2, '0')}`;
};

export interface When {
  firstDay: string;
  lastDay: string;
  startTime: string;
  endTime: string;
}

export const whenOf = (form: Pick<EventForm, 'start_date' | 'end_date'>): When => {
  const a = splitLocal(form.start_date);
  const b = splitLocal(form.end_date);
  return { firstDay: a.date, lastDay: b.date, startTime: a.time, endTime: b.time };
};

const minutesToTime = (m: number): string => {
  const c = Math.max(0, Math.min(m, 23 * 60 + 45));
  return `${String(Math.floor(c / 60)).padStart(2, '0')}:${String(c % 60).padStart(2, '0')}`;
};

/** Form dates from the four pickers. Choosing the first day (or its start) fills what is still empty:
 *  last day = first day, end = start + 2 h. On a one-day event the last day follows the first, and moving
 *  Starts moves Ends by the same amount until Ends is set by hand (`endTouched`). */
export function applyWhen(form: EventForm, next: Partial<When>, opts: { endTouched?: boolean } = {}): EventForm {
  const cur = whenOf(form);
  const w = { ...cur, ...next };
  if (next.firstDay !== undefined && next.lastDay === undefined && cur.firstDay && cur.firstDay === cur.lastDay) w.lastDay = next.firstDay;
  if (w.firstDay && !w.lastDay) w.lastDay = w.firstDay;
  if (w.firstDay && w.lastDay && w.lastDay < w.firstDay) w.lastDay = w.firstDay;
  if (w.firstDay && !w.startTime) w.startTime = '09:00';
  if (next.startTime !== undefined && next.endTime === undefined && cur.startTime && cur.endTime && !opts.endTouched && w.firstDay === w.lastDay) {
    const length = toMinutes(cur.endTime) - toMinutes(cur.startTime);
    if (length > 0 && w.startTime) w.endTime = minutesToTime(toMinutes(w.startTime) + length);
  }
  if (w.startTime && !w.endTime) w.endTime = defaultEndTime(w.startTime);
  return { ...form, start_date: joinLocal(w.firstDay, w.startTime), end_date: joinLocal(w.lastDay, w.endTime) };
}

// --- Hours per day (behind VITE_EVENT_DAYS) ----------------------------------------------------------

const eachDay = (first: string, last: string): string[] => {
  const out: string[] = [];
  if (!first || !last || last < first) return out;
  const [y, m, d] = first.split('-').map(Number);
  for (let i = 0; i < 366; i += 1) {
    const day = new Date(Date.UTC(y, m - 1, d + i)).toISOString().slice(0, 10);
    if (day > last) break;
    out.push(day);
  }
  return out;
};

/** One row per day from the first to the last; rows already there keep their hours, new ones start with the first day's. */
export function syncDayRows(rows: EventDayRow[] | undefined, w: When): EventDayRow[] {
  const known = new Map((rows ?? []).map((r) => [r.date, r]));
  return eachDay(w.firstDay, w.lastDay).map((date) => known.get(date) ?? { date, start: w.startTime, end: w.endTime, closed: false });
}

/** "Copy first day to all": every open row takes the first row's hours. */
export const copyFirstDayToAll = (rows: EventDayRow[]): EventDayRow[] =>
  rows.map((r) => (rows[0] ? { ...r, start: rows[0].start, end: rows[0].end } : r));

/** The event's own start and end follow its first and last open days. */
export function withDayRows(form: EventForm, rows: EventDayRow[]): EventForm {
  const open = rows.filter((r) => !r.closed);
  const first = open[0];
  const last = open[open.length - 1];
  return {
    ...form,
    days: rows,
    ...(first && last ? { start_date: joinLocal(first.date, first.start), end_date: joinLocal(last.date, last.end) } : {}),
  };
}

/** What is wrong with the per-day rows, or null. */
export function dayRowsBlocker(rows: EventDayRow[] | undefined): string | null {
  const open = (rows ?? []).filter((r) => !r.closed);
  if (rows && rows.length > 0 && open.length === 0) return 'Keep at least one day open.';
  const bad = open.find((r) => !r.start || !r.end || r.end <= r.start);
  return bad ? `The end must be after the start on ${bad.date}.` : null;
}

// --- Copy from English (a copy, no AI) -----------------------------------------------------------

export const LANG_FIELDS = [
  { en: 'name_en', es: 'name_es' },
  { en: 'short_description_en', es: 'short_description_es' },
  { en: 'description_en', es: 'description_es' },
] as const;

export type EsField = (typeof LANG_FIELDS)[number]['es'];
export type EsSnapshot = Record<EsField, string>;

export const esSnapshot = (form: EventForm): EsSnapshot => ({
  name_es: form.name_es || '', short_description_es: form.short_description_es || '', description_es: form.description_es || '',
});

/** Some English field has text where its Spanish twin is empty: there is something to copy. */
export const canCopyFromEnglish = (form: EventForm): boolean =>
  LANG_FIELDS.some((f) => (form[f.en] || '').trim() !== '' && !(form[f.es] || '').trim());

export const englishIsEmpty = (form: EventForm): boolean => LANG_FIELDS.every((f) => !(form[f.en] || '').trim());
export const spanishIsEmpty = (form: EventForm): boolean => LANG_FIELDS.every((f) => !(form[f.es] || '').trim());

/** Copies English into the Spanish fields that are still empty; fields already written are kept. */
export function copyFromEnglish(form: EventForm): { form: EventForm; filled: number; before: EsSnapshot } {
  const before = esSnapshot(form);
  const next: EventForm = { ...form };
  let filled = 0;
  LANG_FIELDS.forEach((f) => {
    if ((form[f.en] || '').trim() && !(form[f.es] || '').trim()) {
      next[f.es] = form[f.en];
      filled += 1;
    }
  });
  return { form: next, filled, before };
}

export const restoreSpanish = (form: EventForm, before: EsSnapshot): EventForm => ({ ...form, ...before });

// --- Steps -----------------------------------------------------------------------------------------

/** What Continue needs on each step; `null` when it can go on. */
export function eventStepBlocker(step: EventStep, form: EventForm): string | null {
  if (step === 'event') return (form.name_en || '').trim() || (form.name_es || '').trim() ? null : 'Add a name in English or Spanish.';
  if (step === 'when') {
    const w = whenOf(form);
    if (!w.firstDay || !w.lastDay) return 'Pick the first and the last day.';
    if (!w.startTime || !w.endTime) return 'Set when it starts and ends.';
    if (form.end_date! < form.start_date!) return 'The end must be after the start.';
    return dayRowsBlocker(form.days);
  }
  if (step === 'contact') return form.business_id ? null : 'Select a business.';
  return null;
}

/** The step whose fields a missing required field (eventForm's `missingRequiredFields`) lives in. */
export const stepOfField = (field: 'name' | 'business_id' | 'start_date' | 'end_date'): EventStep =>
  field === 'name' ? 'event' : field === 'business_id' ? 'contact' : 'when';

// --- Review -----------------------------------------------------------------------------------------

const words = (md: string): number => (md.trim() ? md.trim().split(/\s+/).length : 0);

export interface ReviewRow {
  step: EventStep;
  label: string;
  value: string;
  /** Something is missing and the row says so. */
  warn?: boolean;
}

export interface ReviewContext {
  eventTypeName?: string;
  accessLabel?: string;
}

export function reviewRows(form: EventForm, ctx: ReviewContext = {}): ReviewRow[] {
  const name = (form.name_en || form.name_es || '').trim();
  const en = (form.name_en || '').trim() !== '';
  const es = (form.name_es || '').trim() !== '';
  const langs = en && es ? 'EN and ES done' : en ? 'EN done · ES missing' : es ? 'ES done · EN missing' : 'No name yet';
  const desc = words(form.description_en || '') || words(form.description_es || '');
  const images: Array<[string, string | undefined]> = [['Logo', form.logo_url], ['web banner', form.background_img_url], ['mobile banner', form.banner_mobile_url]];
  const have = images.filter(([, v]) => v).map(([k]) => k);
  const missingImages = images.filter(([, v]) => !v).map(([k]) => k);
  const w = whenOf(form);
  const days = dayCount(w.firstDay, w.lastDay);
  const when = w.firstDay
    ? `${formatEventDate(form.start_date)}${days > 1 ? ` – ${formatEventDate(form.end_date)}` : ''} · ${days > 1 ? `${days} days · ` : ''}${formatTime12(w.startTime)} – ${formatTime12(w.endTime)}`
    : 'Not set';
  const contact = [form.phone_number, form.instagram_url, form.website_url].filter(Boolean).join(' · ');
  const access = [ctx.eventTypeName, ctx.accessLabel, form.capacity ? `${form.capacity} people` : ''].filter(Boolean).join(' · ');
  return [
    { step: 'event', label: 'Event', value: name ? `${name} · ${langs}${desc ? ` · description ${desc} words` : ''}` : langs, warn: !name },
    { step: 'event', label: 'Images', value: have.length ? `${have.join(' · ')}${missingImages.length ? ` · ${missingImages.join(', ')} missing` : ''}` : 'No pictures yet (optional until you publish)' },
    { step: 'when', label: 'When', value: when, warn: !w.firstDay },
    { step: 'when', label: 'Where', value: (form.address || '').trim() || 'No address' },
    { step: 'contact', label: 'Contact', value: contact || 'None' },
    { step: 'contact', label: 'Access', value: access || 'None', warn: !form.business_id },
  ];
}

